import React, { FunctionComponent, useState } from 'react'

import { useMutation } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button, ButtonVariant } from '@/atoms/button'
import { ErrorMessage, Form, FormControl } from '@/components/form-elements'
import { useNavigation } from '@/context/navigation-context'
import { LOGIN_SA_USER } from '@/graphql/public-pages/login/mutations'
import { setRequestError } from '@/helpers/error'
import * as storageUtils from '@/helpers/storage-utils'
import { enumToInitialValues } from '@/helpers/table'
import { LoginFieldsNames, LoginSAData, LoginSAVariables, UserRole } from '@/models/auth'
import { FormControlType } from '@/models/common'
import { getDefaultUrl, isPublicPath, path } from '@/navigation'
import { Space } from '@/styles'
import { loginSchema } from '@/validation-schemas/auth'

const initialValues = enumToInitialValues<LoginSAVariables>(LoginFieldsNames)

const LoginForm: FunctionComponent = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { previousLocation } = useNavigation()
  const [errorMessage, setErrorMessage] = useState('')
  const [loginSAUser, { loading }] = useMutation<LoginSAData, LoginSAVariables>(LOGIN_SA_USER, {
    onCompleted({ signInSA: { client } }) {
      errorMessage && setErrorMessage('')
      const role = client ? UserRole.CLIENT : UserRole.BACK_OFFICE_USER
      storageUtils.saveRoleToLocalStorage(role)
      // Redirect to the previous page if it exists and is not the login page
      if (previousLocation && !isPublicPath(previousLocation?.pathname)) {
        navigate(previousLocation.pathname)
      } else {
        // If there is no previous location or it is the login page, navigate to the default URL
        navigate(getDefaultUrl(role))
      }
    },
    onError({ graphQLErrors, networkError }) {
      setRequestError({ networkError, graphQLErrors }, setErrorMessage)
    },
  })

  const handleLogin = async ({ email, password }: LoginSAVariables): Promise<void> => {
    await loginSAUser({ variables: { email, password } })
  }

  return (
    <Form<LoginSAVariables> initialValues={initialValues} onSubmit={handleLogin} validationSchema={loginSchema}>
      <FormControl
        label={t('global.label.email')}
        name={LoginFieldsNames.EMAIL}
        control={FormControlType.PUBLIC_TEXT}
        fieldProps={{ mb: Space.S20 }}
      />
      <FormControl
        type="password"
        label={t('login.password_label')}
        linkText={t('login.forgot_password_link')}
        linkPath={path.resetPassword}
        name={LoginFieldsNames.PASSWORD}
        control={FormControlType.PUBLIC_TEXT}
        fieldProps={{ mb: Space.S24 }}
      />

      {errorMessage && <ErrorMessage display="block" error={errorMessage} />}
      <Button variant={ButtonVariant.PUBLIC} disabled={!errorMessage && loading} loading={!errorMessage && loading} type="submit" fullWidth>
        {t('login.login_button')}
      </Button>
    </Form>
  )
}

export default LoginForm
